/**
 * Battleship Game Logic V2
 * Clean implementation based solely on battleship-rules.md
 *
 * Key principles:
 * - Immutable state updates (no mutations)
 * - Deep copying all data structures
 * - Clear, straightforward logic
 * - Robust error handling
 */

// Constants
export const GRID_SIZE = 10;

export const SHIPS = [
  { id: 'carrier', name: 'Aircraft Carrier', size: 5 },
  { id: 'battleship', name: 'Battleship', size: 4 },
  { id: 'cruiser', name: 'Cruiser', size: 3 },
  { id: 'submarine', name: 'Submarine', size: 3 },
  { id: 'destroyer', name: 'Destroyer', size: 2 },
];

/**
 * Create an empty 10x10 grid filled with nulls
 */
export const createEmptyGrid = () => {
  const grid = [];
  for (let i = 0; i < GRID_SIZE; i++) {
    const row = [];
    for (let j = 0; j < GRID_SIZE; j++) {
      row.push(null);
    }
    grid.push(row);
  }
  return grid;
};

/**
 * Deep copy a grid to avoid mutations
 */
const deepCopyGrid = (grid) => {
  if (!grid || !Array.isArray(grid)) {
    return createEmptyGrid();
  }

  const newGrid = [];
  for (let i = 0; i < GRID_SIZE; i++) {
    const row = [];
    for (let j = 0; j < GRID_SIZE; j++) {
      const cell = grid[i] && grid[i][j];
      row.push(cell === undefined ? null : cell);
    }
    newGrid.push(row);
  }
  return newGrid;
};

/**
 * Deep copy a ship object
 */
const deepCopyShip = (ship) => {
  return {
    id: ship.id,
    name: ship.name,
    size: ship.size,
    row: ship.row,
    col: ship.col,
    isHorizontal: ship.isHorizontal,
    positions: ship.positions.map(pos => ({
      row: pos.row,
      col: pos.col,
      hit: pos.hit
    })),
    sunk: ship.sunk
  };
};

/**
 * Deep copy an array of ships
 */
const deepCopyShips = (ships) => {
  if (!ships || !Array.isArray(ships)) {
    return [];
  }
  return ships.map(ship => deepCopyShip(ship));
};

/**
 * Create initial game state for a new game
 * Randomly determines who goes first
 */
export const createInitialGameState = (hostId, guestId) => {
  // Randomly choose who goes first
  const firstPlayer = Math.random() < 0.5 ? hostId : guestId;

  return {
    phase: 'placement',           // 'placement' or 'battle'
    currentTurn: firstPlayer,     // Player ID whose turn it is
    hostId: hostId,
    guestId: guestId,
    hostReady: false,
    guestReady: false,
    hostShips: [],                // Array of ship objects for host
    guestShips: [],               // Array of ship objects for guest
    hostAttacks: createEmptyGrid(), // Host's attacks on guest
    guestAttacks: createEmptyGrid(), // Guest's attacks on host
    isGameOver: false,
    winner: null,
    lastAttack: null,             // Info about the most recent attack
    hostShipsSunk: [],            // Array of ship IDs that host has sunk
    guestShipsSunk: []            // Array of ship IDs that guest has sunk
  };
};

/**
 * Validate ship placement
 * Returns true if placement is valid, false otherwise
 */
export const isValidPlacement = (existingShips, shipToPlace, row, col, isHorizontal) => {
  // Check bounds
  if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) {
    return false;
  }

  // Check if ship fits within grid
  if (isHorizontal) {
    if (col + shipToPlace.size > GRID_SIZE) return false;
  } else {
    if (row + shipToPlace.size > GRID_SIZE) return false;
  }

  // Create a set of occupied positions
  const occupiedPositions = new Set();
  existingShips.forEach(ship => {
    ship.positions.forEach(pos => {
      occupiedPositions.add(`${pos.row},${pos.col}`);
    });
  });

  // Check if new ship overlaps with existing ships
  for (let i = 0; i < shipToPlace.size; i++) {
    const r = isHorizontal ? row : row + i;
    const c = isHorizontal ? col + i : col;
    const key = `${r},${c}`;

    if (occupiedPositions.has(key)) {
      return false; // Overlaps with existing ship
    }
  }

  return true;
};

/**
 * Place a ship on the board
 * Returns new ships array with the ship added (or replaced if already exists)
 */
export const placeShip = (currentShips, shipId, row, col, isHorizontal) => {
  const shipTemplate = SHIPS.find(s => s.id === shipId);
  if (!shipTemplate) {
    return currentShips;
  }

  // Remove existing ship with same ID if present
  const shipsWithoutThisOne = currentShips.filter(s => s.id !== shipId);

  // Validate placement
  if (!isValidPlacement(shipsWithoutThisOne, shipTemplate, row, col, isHorizontal)) {
    return currentShips; // Return unchanged if invalid
  }

  // Create positions array
  const positions = [];
  for (let i = 0; i < shipTemplate.size; i++) {
    positions.push({
      row: isHorizontal ? row : row + i,
      col: isHorizontal ? col + i : col,
      hit: false
    });
  }

  // Create new ship object
  const newShip = {
    id: shipTemplate.id,
    name: shipTemplate.name,
    size: shipTemplate.size,
    row: row,
    col: col,
    isHorizontal: isHorizontal,
    positions: positions,
    sunk: false
  };

  // Return new ships array with new ship added
  return [...shipsWithoutThisOne, newShip];
};

/**
 * Randomly place all ships on the board
 */
export const randomPlaceShips = () => {
  let ships = [];

  for (const shipTemplate of SHIPS) {
    let placed = false;
    let attempts = 0;
    const maxAttempts = 100;

    while (!placed && attempts < maxAttempts) {
      const isHorizontal = Math.random() < 0.5;
      const row = Math.floor(Math.random() * GRID_SIZE);
      const col = Math.floor(Math.random() * GRID_SIZE);

      const newShips = placeShip(ships, shipTemplate.id, row, col, isHorizontal);

      if (newShips.length > ships.length) {
        // Ship was successfully placed
        ships = newShips;
        placed = true;
      }

      attempts++;
    }

    if (!placed) {
      // Failed to place ship, start over
      return randomPlaceShips();
    }
  }

  return ships;
};

/**
 * Check if all ships have been placed
 */
export const allShipsPlaced = (ships) => {
  return ships && ships.length === SHIPS.length;
};

/**
 * Mark a player as ready (all ships placed)
 * Returns new game state
 */
export const markPlayerReady = (gameState, playerId, ships) => {
  const isHost = playerId === gameState.hostId;

  const newState = {
    ...gameState,
    hostReady: isHost ? true : gameState.hostReady,
    guestReady: isHost ? gameState.guestReady : true,
    hostShips: isHost ? deepCopyShips(ships) : deepCopyShips(gameState.hostShips),
    guestShips: isHost ? deepCopyShips(gameState.guestShips) : deepCopyShips(ships)
  };

  // If both players are ready, start the battle phase
  if (newState.hostReady && newState.guestReady) {
    newState.phase = 'battle';
  }

  return newState;
};

/**
 * Process an attack at the specified coordinates
 * Returns new game state with attack results
 */
export const processAttack = (gameState, attackingPlayerId, row, col) => {
  // Validate it's the attacker's turn
  if (gameState.currentTurn !== attackingPlayerId) {
    return { ...gameState, error: 'Not your turn' };
  }

  // Validate game is in battle phase
  if (gameState.phase !== 'battle') {
    return { ...gameState, error: 'Game is not in battle phase' };
  }

  // Validate coordinates
  if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) {
    return { ...gameState, error: 'Invalid coordinates' };
  }

  const isHost = attackingPlayerId === gameState.hostId;

  // Get attack grid and defending ships
  const attackGrid = isHost ? gameState.hostAttacks : gameState.guestAttacks;
  const defendingShips = isHost ? gameState.guestShips : gameState.hostShips;

  // Check if already attacked
  if (attackGrid[row][col] !== null) {
    return { ...gameState, error: 'Already attacked this square' };
  }

  // Deep copy everything we'll modify
  const newAttackGrid = deepCopyGrid(attackGrid);
  const newDefendingShips = deepCopyShips(defendingShips);

  // Check if attack hits a ship
  let hit = false;
  let hitShipIndex = -1;

  for (let i = 0; i < newDefendingShips.length; i++) {
    const ship = newDefendingShips[i];
    for (let j = 0; j < ship.positions.length; j++) {
      const pos = ship.positions[j];
      if (pos.row === row && pos.col === col) {
        // Hit!
        hit = true;
        hitShipIndex = i;
        pos.hit = true;
        break;
      }
    }
    if (hit) break;
  }

  // Update attack grid
  newAttackGrid[row][col] = hit ? 'hit' : 'miss';

  // Check if ship was sunk
  let sunkShip = null;
  if (hit && hitShipIndex >= 0) {
    const hitShip = newDefendingShips[hitShipIndex];
    const allHit = hitShip.positions.every(p => p.hit);

    if (allHit) {
      hitShip.sunk = true;
      sunkShip = hitShip;
    }
  }

  // Update sunk ships list
  const currentSunkShips = isHost
    ? (gameState.hostShipsSunk || [])
    : (gameState.guestShipsSunk || []);
  const newSunkShips = sunkShip && !currentSunkShips.includes(sunkShip.id)
    ? [...currentSunkShips, sunkShip.id]
    : [...currentSunkShips];

  // Check if all ships are sunk (win condition)
  const allShipsSunk = newDefendingShips.every(ship => ship.sunk);

  // Switch turns
  const nextTurn = gameState.currentTurn === gameState.hostId
    ? gameState.guestId
    : gameState.hostId;

  // Build new game state
  const newState = {
    ...gameState,
    hostAttacks: isHost ? newAttackGrid : deepCopyGrid(gameState.hostAttacks),
    guestAttacks: isHost ? deepCopyGrid(gameState.guestAttacks) : newAttackGrid,
    hostShips: isHost ? deepCopyShips(gameState.hostShips) : newDefendingShips,
    guestShips: isHost ? newDefendingShips : deepCopyShips(gameState.guestShips),
    hostShipsSunk: isHost ? newSunkShips : [...(gameState.hostShipsSunk || [])],
    guestShipsSunk: isHost ? [...(gameState.guestShipsSunk || [])] : newSunkShips,
    currentTurn: nextTurn,
    lastAttack: {
      row: row,
      col: col,
      result: hit ? 'hit' : 'miss',
      sunkShip: sunkShip ? sunkShip.name : null,
      attacker: attackingPlayerId
    },
    isGameOver: allShipsSunk,
    winner: allShipsSunk ? attackingPlayerId : null
  };

  return newState;
};

/**
 * Get ship information by ID
 */
export const getShipById = (shipId) => {
  return SHIPS.find(s => s.id === shipId);
};
